import React, { useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import { ScrollArea } from "@edusogno/primitives/scroll-area.js";
// import { useDownload } from "@/utils/hooks/useDownload";
import { blobToBase64 } from "@/utils/string.ts";
import { useDownloadFileQuery } from "@/data/file.ts";
import { ExerciseDONE, ExerciseTODO } from "@/types";
import cn from "classnames";
import Spinner from "@edusogno/primitives/spinner";
import PDFViewer from "@/components/ui/PDFViewer";
import AnswerSlot from "@/components/v3/exercises/AnswerSlot.tsx";
import downloadIcon from "@/assets/svg/download.svg";
// import downloadIcon from "@/assets/svg/downloadIcon.svg";
import { Tabs, TabsList, TabsTrigger } from "@edusogno/primitives/tabs.js";
import { useDownload } from "@/utils/hooks/useDownload";
import { useTranslation } from "react-i18next";
import ImgViewer from "@/components/ui/ImgViewer";

interface AnswerSheetProps {
  exercise?: ExerciseTODO | ExerciseDONE;
  timeStart?: Dayjs;
  renderSlots?: (
    exercise:
      | [string, { question_type: string; correct_answer: string }][]
      | undefined,
  ) => React.ReactNode;
  id: number | undefined;
}

function ReadingAnswerSheet({ exercise, id, renderSlots }: AnswerSheetProps) {
  const { download } = useDownload();
  const { t } = useTranslation();
  const [pdf, setPdf] = useState<string | null>(null);
  const [imagesGroup, setImagesGroup] = useState<string[] | null>(null);
  const [questionsPdf, setQuestionsPdf] = useState<string | null>(null);
  // const [showPDF, setShowPDF] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState("Questions");
  const [fileType, setFileType] = useState("media1");
  const [questionType, setQuestionType] = useState("question1");
  const [qImagesGroup, setQImagesGroup] = useState<string[] | null>(null);

  const images = exercise?.files?.images && exercise.files.images.length > 0 ? exercise.files.images : null;
  const qImages = exercise?.files?.["q-images"] && exercise.files?.["q-images"].length > 0 ? exercise.files?.["q-images"] : null;

  const [pdfCache, setPdfCache] = useState<{ [key: string]: string | null }>(
    {},
  );
  const [questionsPdfCache, setQuestionsPdfCache] = useState<{
    [key: string]: string | null;
  }>({});

  const { isLoading: isPDFLoading, blob } = useDownloadFileQuery(
    { id: (id as number) ?? exercise?.id, type: fileType },
    fileType !== undefined && !pdfCache[fileType],
  );

  const { blob: questionsBlob } = useDownloadFileQuery(
    { id: (id as number) ?? exercise?.id, type: questionType },
    questionType !== undefined && !questionsPdfCache[questionType],
  );

  const handleTabChange = (tabName: string) => {
    setActiveTab(tabName);
  };

  const handleFileChange = (type: string) => {
    setFileType(type);
    if (pdfCache[type]) {
      setPdf(pdfCache[type]);
    }

    if (type === "media1") {
      setImagesGroup(images && images[0]);
    } else if (type === "media2") {
      setImagesGroup(images && images[1]);
    } else if (type === "media3") {
      setImagesGroup(images && images[2]);
    } else {
      setImagesGroup(null);
    }
  };

  const handleQuestionChange = (type: string) => {
    setQuestionType(type);
    if (questionsPdfCache[type]) {
      setQuestionsPdf(questionsPdfCache[type]);
    }

    if (type === "question1") {
      setQImagesGroup(qImages && qImages[0]);
    } else if (type === "question2") {
      setQImagesGroup(qImages && qImages[1]);
    } else if (type === "question3") {
      setQImagesGroup(qImages && qImages[2]);
    } else {
      setQImagesGroup(null);
    }
  };

  useEffect(() => {
    if (!blob || pdfCache[fileType]) return;
    void (async () => {
      const result = await blobToBase64(blob);
      setPdf(result);
      setPdfCache((prev) => ({ ...prev, [fileType]: result }));

      const idx = fileType === "media1" ? 0 : fileType === "media2" ? 1 : 2;
      setImagesGroup(images && images[idx]);

    })();
  }, [blob, fileType, pdfCache, images]);

  useEffect(() => {
    if (!questionsBlob || questionsPdfCache[questionType]) return;
    void (async () => {
      const result = await blobToBase64(questionsBlob);
      setQuestionsPdf(result);
      setQuestionsPdfCache((prev) => ({ ...prev, [questionType]: result }));

      const idx = questionType === "question1" ? 0 : questionType === "question2" ? 1 : 2;
      setQImagesGroup(qImages && qImages[idx]);
    })();
  }, [questionsBlob, questionType, questionsPdfCache, qImages]);

  const arrayParts = [
    { text: "Part 1", mediaValue: "media1", questionValue: "question1" },
    { text: "Part 2", mediaValue: "media2", questionValue: "question2" },
    { text: "Part 3", mediaValue: "media3", questionValue: "question3" },
  ];

  return (
    <div className="flex flex-col xl:flex-row gap-4 w-full flex-[1_1_0] min-h-0 h-full">
      
      <div className="flex flex-col w-full h-full until-xl:overflow-y-auto">
        <div className="flex flex-col w-full min-h-full gap-4 xl:flex-row">
          {/* PDF SECTION */}
          <section
            className={cn(
              "rounded-[10px] flex flex-col h-full",
              "until-xl:min-h-[30vh] xl:min-h-auto w-full flex-[1_1_100%]",
            )}
          >
            <Tabs defaultValue="Part 1" className="flex gap-3">
              {arrayParts.map((part) => (
                <TabsList
                  key={part.text}
                  className="p-0 rounded-[10px] h-[30px] md:h-[30px]"
                >
                  <TabsTrigger
                    value={part.text}
                    onClick={() => {
                      handleFileChange(part.mediaValue);
                      handleQuestionChange(part.questionValue);
                    }}
                    className="active:border-none active:outline-0 h-[30px] md:h-[30px] px-4 lg:w-[60px] data-[state=active]:bg-black text-black text-sm rounded-[10px] data-[state=active]:text-white"
                  >
                    {part.text}
                  </TabsTrigger>
                </TabsList>
              ))}
              {/* <button
            onClick={() =>
              download({ id: (id as number) ?? exercise?.id })
            }
            className="h-[30px] md:h-[30px] w-[60px] rounded-[10px]"
          >
            <img
              src={downloadIcon}
              alt="download icon"
              className="w-[30px]"
            />
          </button> */}
            </Tabs>
            <div
              className={cn(
                "rounded-[10px] flex flex-col xl:flex-row w-full h-full bg-white mt-4 min-h-0",
                "until-xl:min-h-[30vh] xl:min-h-auto flex-[1_1_100%]",
              )}
            >
              {isPDFLoading && !imagesGroup ? (
                <div className="flex items-center justify-center w-full h-full col-span-3">
                  <Spinner className="!h-12 !w-12 border-t-light" />
                </div>
              ) : (

                imagesGroup ? (
                  <ImgViewer images={imagesGroup} />
                ) : (
                  <PDFViewer fileBase64={pdf} />
                )
              )}
            </div>
          </section>
          {/* QUESTIONS/ANSWER SHEET SECTION */}
          <section className="flex flex-col flex-grow w-full min-h-full gap-4 until-xl:pt-11">
            <div className="flex flex-row items-center justify-between">
              <Tabs
                defaultValue="Questions"
                className="flex items-center justify-between w-full"
              >
                <TabsList className="p-0 rounded-[10px]  h-[30px] md:h-[30px] flex gap-3 w-auto">
                  <TabsTrigger
                    onClick={() => handleTabChange("Questions")}
                    value="Questions"
                    className="active:border-none active:outline-0 lg:w-[120px] h-[30px] md:h-[30px] xl:px-4 data-[state=active]:bg-black text-black text-sm rounded-[10px] data-[state=active]:text-white"
                  >
                    {t("v3.questions", { defaultValue: "Questions" })}
                  </TabsTrigger>
                  <TabsTrigger
                    onClick={() => handleTabChange("Answer_sheet")}
                    value="Answer sheet"
                    className="active:border-none active:outline-0 lg:w-[120px] h-[30px] md:h-[30px] px-[25px] xl:px-4 data-[state=active]:bg-black text-black text-sm rounded-[10px] data-[state=active]:text-white"
                  >
                    {t("v3.answer_sheet", { defaultValue: "Answer Sheet" })}
                  </TabsTrigger>
                </TabsList>
                <button
                  onClick={() =>
                    download({ id: (id as number) ?? exercise?.id })
                  }
                  className="h-[30px] md:h-[30px] w-[30px] rounded-[10px] bg-black  flex justify-center items-center"
                >
                  <img
                    src={downloadIcon}
                    alt="download icon"
                    className="w-[20px] h-[20px]"
                  />
                </button>
              </Tabs>
            </div>
            <div
              className={cn(
                "rounded-[10px] flex flex-col xl:flex-row w-full h-full bg-white min-h-0",
                { hidden: activeTab !== "Questions" },
              )}
            >
              {/* <PDFViewer fileBase64={questionsPdf} /> */}

              {qImagesGroup ? (
                <ImgViewer images={qImagesGroup} />
              ) : (
                <PDFViewer fileBase64={questionsPdf} />
              )}
            </div>
            <div
              className={cn("rounded-[10px] w-full h-full min-h-0", {
                hidden: activeTab !== "Answer_sheet",
              })}
            >
              {renderSlots ? (
                renderSlots(
                  exercise?.questions && Object.entries(exercise.questions),
                )
              ) : (
                <ScrollArea className="h-full min-h-0 ">
                  <div
                    id="form-ex"
                    className="grid grid-cols-[repeat(auto-fill,minmax(247px,auto))] gap-[12px] w-full"
                  >
                    {exercise?.questions &&
                      Object.entries(exercise.questions).map(([key, q]) => (
                        <AnswerSlot
                          key={key}
                          type={
                            q.question_type === "testo" ? "write" : "select"
                          }
                          number={key}
                          options={
                            q.question_type !== "testo"
                              ? q.question_type.split(",")
                              : []
                          }
                        />
                      ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default ReadingAnswerSheet;
